import { Box } from "@mui/material";
import { useMemo, useState } from "react";
import { NumberInput, ProductStockBadge } from "$components";
import { DataTableWithContext, EnumDataRowType } from "$components/DataTable";
import SideMenu from "$components/SideMenuProductList/SideMenu";
import useAuth from "$hooks/useAuth";
import useReferencingCategory from "$hooks/useReferencingCategory";
import { getProductStock } from "$services/Products.service";
import { useStyles } from "../styles/ProductsLists.styles";
import { useDebounce } from "$utils/customHooks";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  companyId: string;
  addToCart: any;
  cart: any;
  handleClickProduct: any;
};

function ProductsList({
  companyId,
  addToCart,
  cart,
  handleClickProduct,
}: Props) {
  const [filterByCateg, setFilterByCateg] = useState<string>("");
  const [filterSearch, setFilterSearch] = useState<string>("");

  const { referencingCategory } = useReferencingCategory(companyId);
  const classes = useStyles();
  const { isContributor } = useAuth();

  const debouncedFilterSearch = useDebounce(filterSearch);
  const debouncedFilterByCateg = useDebounce(filterByCateg);

  const externalState = useMemo(() => {
    const filters = {} as any;
    filters.category = debouncedFilterByCateg;
    filters.$orSearch = debouncedFilterSearch;

    if (!isContributor()) {
      filters.referencingCategory = referencingCategory?.id;
    }

    return {
      filters: { ...filters, company: companyId, isActive: true },
      pagination: { page: 1 },
    } as any;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    referencingCategory,
    companyId,
    debouncedFilterByCateg,
    debouncedFilterSearch,
  ]);

  const headerData = [
    {
      id: "designation",
      label: "designation",
      cell: {
        onClick: (p) => handleClickProduct(p),
        type: EnumDataRowType.EmptyLink,
        value: ({ designation, reference }) => {
          return {
            title: designation,
            subtitle: `Ref: ${reference}`,
          };
        },
      },
    },
    {
      id: "unit",
      label: "unit",
      width: 72,
      onClick: (p) => handleClickProduct(p),
      cell: {
        value: ({ unit }) => unit,
      },
    },
    {
      id: "quantity",
      label: "quantity",
      width: 164,
      onClick: (p) => handleClickProduct(p),
      cell: {
        value: ({ _id }) => (
          <ProductStockBadge productId={_id} companyId={companyId} />
        ),
      },
    },
    {
      id: "actions",
      label: "actions",
      width: 152,
      cell: {
        value: ({ _id }) => {
          const productQty =
            cart.find((pcart) => pcart.productID === _id)?.qty || 0;
          return (
            <NumberInput
              value={productQty}
              onChange={(val) => addToCart(_id, val)}
            />
          );
        },
      },
    },
  ];

  return (
    <Box className={classes.bottomContent}>
      <SideMenu
        filterSearch={filterSearch}
        setFilterSearch={setFilterSearch}
        filterByCategory={filterByCateg}
        setFilterByCategory={setFilterByCateg}
        companyId={companyId}
        withReferencing={false}
      />

      <DataTableWithContext
        externalState={externalState}
        className={classes.tableContainer}
        rowsPerPage={15}
        thead={headerData}
        queryFunction={getProductStock}
        queryFunctionName={CacheInvalidationAction.GET_PRODUCT_STOCK}
        data-testid={`productStock_${companyId}`}
      />
    </Box>
  );
}

export default ProductsList;
